// Canonical Terms and Conditions document.
import { buildSupportMailto } from '../../../configs/brandLinks';
import { defineDocument, link, list, p, section, strong, text, } from '../../schema';
const supportEmail = 'poppy@airheadgames.com';
const supportMailto = buildSupportMailto();
export const termsDocument = defineDocument({
    id: 'legal:terms',
    kind: 'legal',
    slug: 'terms',
    route: '/terms',
    seo: {
        title: 'Terms of Service',
        description: 'Read our Terms of Service. Learn how we protect your data and what to expect when using Airhead Games.',
    },
    hero: {
        title: [text('Terms & Conditions')],
        description: [
            text('Learn how we protect your data and what to expect when using Airhead Games.'),
        ],
        showDescription: true,
        background: 'gradient-card',
        backgroundAccentFamily: 'lime',
    },
    meta: {
        effectiveDate: new Date('2026-02-28'),
        updatedAt: new Date('2026-02-28'),
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'effective-date',
            title: 'Effective Date',
            blocks: [
                p('The following Terms & Conditions for Airhead Games are effective as of February 28, 2026.'),
            ],
        }),
        section({
            id: 'acceptance-of-terms',
            title: '1. Acceptance of Terms',
            blocks: [
                p('By accessing or using Airhead Games ("Service"), you agree to be bound by these Terms & Conditions ("Terms"). If you do not agree, please do not use the Service.'),
            ],
        }),
        section({
            id: 'operator-of-the-service',
            title: '2. Operator of the Service',
            blocks: [
                p([
                    text('Airhead Games is operated by an independent software developer ("we," "our," "us"). For any inquiries, please contact '),
                    link(supportMailto, supportEmail),
                    text('.'),
                ]),
            ],
        }),
        section({
            id: 'eligibility',
            title: '3. Eligibility',
            blocks: [
                p('You must be at least 13 years old to use the Service. By using the Service, you represent that you meet this age requirement.'),
            ],
        }),
        section({
            id: 'user-accounts',
            title: '4. User Accounts',
            blocks: [
                list({
                    items: [
                        [
                            strong('Account Creation:'),
                            text(' You may create an account by providing a valid email address or by signing up with your Twitch account. Email accounts require verification of your email address.'),
                        ],
                        [
                            strong('Account Security:'),
                            text(' You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.'),
                        ],
                        [
                            strong('Account Termination:'),
                            text(' We reserve the right to suspend or terminate any account at any time, with or without notice, for reasons including but not limited to violation of these Terms or any behavior deemed abusive or harmful to the Service or its users.'),
                        ],
                    ],
                }),
            ],
        }),
        section({
            id: 'intellectual-property-and-content-ownership',
            title: '5. Intellectual Property and Content Ownership',
            sections: [
                section({
                    id: 'our-intellectual-property',
                    title: 'Our Intellectual Property (IP)',
                    blocks: [
                        p("All game content, including content generated by the Service's AI, as well as all trademarks, logos, graphics, designs, text, and other materials provided by us, are owned solely by Airhead Games. Users are granted a limited, revocable, non-exclusive license to access and use the Service's content for personal, non-commercial purposes only. Any reproduction, modification, distribution, or commercial use of AI-generated content without explicit written permission from Airhead Games is strictly prohibited."),
                    ],
                }),
                section({
                    id: 'user-provided-content',
                    title: 'User-Provided Content',
                    blocks: [
                        p('By inputting any text, data, or other materials ("User Content") into the Service, you grant Airhead Games a worldwide, non-exclusive, royalty-free, irrevocable, sublicensable, and transferable license to use, reproduce, modify, create derivative works from, display, and perform your User Content solely in connection with operating and improving the Service. You represent and warrant that you have all necessary rights to grant this license and that your User Content does not infringe upon any third-party rights. You retain ownership of any User Content you provide, subject to the license granted to us.'),
                    ],
                }),
            ],
        }),
        section({
            id: 'ai-generated-content',
            title: '6. AI-Generated Content',
            sections: [
                section({
                    id: 'ai-generated-content-disclaimer',
                    title: 'Disclaimer',
                    blocks: [
                        p('The Service uses AI to generate content. While we strive to align content with user preferences (e.g., standard, family-friendly, edgy), we cannot fully control the AI output. Content may occasionally be inappropriate or unexpected. By using the Service, you acknowledge and accept this risk. We do not guarantee the AI-generated content will meet your expectations, be accurate, or be free of objectionable elements.'),
                    ],
                }),
                section({
                    id: 'ai-generated-content-how-its-used',
                    title: "How It's Used",
                    blocks: [
                        list({
                            items: [
                                [
                                    strong('Dynamic game content:'),
                                    text(' Each game uses AI in a unique way to generate fresh prompts, answers, images, or other game-specific elements. No two turns will ever play the same, so nothing feels stale or recycled.'),
                                ],
                                [
                                    strong('Dynamic host content:'),
                                    text(' Poppy calls out players by name, riffs on answers, comments on scores, and weaves players into the fabric of the game. Every round is highly personalized and unique to your group of players.'),
                                ],
                                [
                                    strong('Service operations:'),
                                    text(' We may use AI to help create, market, and operate the Service.'),
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'purchases',
            title: '7. Purchases',
            sections: [
                section({
                    id: 'loons',
                    title: 'Loons',
                    blocks: [
                        p('Loons are game credits used to play Airhead Games. The number of Loons per game is variable and we reserve the right to adjust the price per game at our discretion. Loons do not expire and remain in your account until used. Loons have no cash value and are non-transferable.'),
                    ],
                }),
                section({
                    id: 'payments',
                    title: 'Payments',
                    blocks: [
                        p([
                            text('Loons are purchased in packs as one-time transactions through the Service. Payments are processed through Stripe; by purchasing you agree to their '),
                            link('https://stripe.com/legal', 'payment processing terms'),
                            text('.'),
                        ]),
                    ],
                }),
                section({
                    id: 'refunds',
                    title: 'Refunds',
                    blocks: [
                        p([
                            text('Refunds may be issued at our discretion in certain circumstances, such as technical issues preventing access to the Service. To request a refund, contact us at '),
                            link(supportMailto, supportEmail),
                            text('. We reserve the right to determine eligibility for any refund requests.'),
                        ]),
                    ],
                }),
            ],
        }),
        section({
            id: 'privacy',
            title: '8. Privacy and Data Use',
            sections: [
                section({
                    id: 'data-collected',
                    title: 'Data Collected',
                    blocks: [
                        p('We collect the following data:'),
                        list({
                            items: [
                                'Your email address.',
                                'Your Twitch ID (if you sign up with Twitch).',
                                'Your user preferences.',
                                'Your game activity.',
                                'Your purchase history.',
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'data-use',
                    title: 'Data Use',
                    blocks: [
                        list({
                            items: [
                                [
                                    strong('Account Management:'),
                                    text(' Your email and/or Twitch ID are used for account creation and verification, depending on your chosen login method. Your purchase history is a high-level record of Loon purchases and refunds used to maintain your account balance accurately; these records do not contain any Personally Identifiable Information (PII) or other sensitive information.'),
                                ],
                                [
                                    strong('Service Communication:'),
                                    text(' We may use your email to contact you about our Services or provide support.'),
                                ],
                                [
                                    strong('Service Operations:'),
                                    text(' Your user preferences and game activity are used for Service operations, analytics, and troubleshooting.'),
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'data-protection',
                    title: 'Data Protection',
                    blocks: [
                        list({
                            items: [
                                [
                                    strong('GDPR Compliance:'),
                                    text(' If you are located in the EU, you have the right to access, rectify, and request deletion of your personal data. You can exercise these rights by contacting '),
                                    link(supportMailto, supportEmail),
                                    text('.'),
                                ],
                                [
                                    strong('CCPA Compliance:'),
                                    text(' California residents have the right to request access to and deletion of their personal data, and to opt-out of the sale of their personal data. To exercise these rights, please contact '),
                                    link(supportMailto, supportEmail),
                                    text('.'),
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'data-deletion',
                    title: 'Data Deletion',
                    blocks: [
                        p([
                            text('You may request deletion of your account and associated user information at any time by contacting '),
                            link(supportMailto, supportEmail),
                            text('. We will comply with such requests in accordance with applicable laws. Please note that upon account deletion, any remaining Loon balance will be permanently forfeited and is not eligible for refund or transfer.'),
                        ]),
                    ],
                }),
                section({
                    id: 'no-separate-privacy-policy',
                    title: 'No Separate Privacy Policy',
                    blocks: [
                        p('By using the Service, you consent to the collection and use of your data as described in these Terms. This constitutes our Privacy Policy.'),
                    ],
                }),
            ],
        }),
        section({
            id: 'third-party-tools-and-services',
            title: '9. Third-Party Tools and Services',
            blocks: [
                p('The Service integrates third-party tools and services. By using the Service, you acknowledge that these third parties may have their own terms and policies. We are not responsible for their content, accuracy, or availability. You agree to comply with any additional terms required by these third parties.'),
            ],
        }),
        section({
            id: 'disclaimers-and-limitation-of-liability',
            title: '10. Disclaimers and Limitation of Liability',
            blocks: [
                list({
                    items: [
                        [
                            strong('"As Is" Basis:'),
                            text(' The Service is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied.'),
                        ],
                        [
                            strong('No Warranty for Uptime or Performance:'),
                            text(' We do not guarantee uninterrupted access, security, or error-free performance.'),
                        ],
                        [
                            strong('Limitation of Liability:'),
                            text(' To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, consequential, or special damages arising out of or in connection with your use of the Service, even if we have been advised of the possibility of such damages.'),
                        ],
                        [
                            strong('Personal Liability:'),
                            text(' As an independent developer, our total liability for any claims related to the Service shall be limited to the value of your unused Loon balance at the time of the claim, up to a maximum of $50.'),
                        ],
                    ],
                }),
            ],
        }),
        section({
            id: 'governing-law-and-dispute-resolution',
            title: '11. Governing Law and Dispute Resolution',
            sections: [
                section({
                    id: 'governing-law',
                    title: 'Governing Law',
                    blocks: [
                        p('These Terms shall be governed by and construed in accordance with the laws of the State of California, USA, without regard to its conflict of laws principles.'),
                    ],
                }),
                section({
                    id: 'jurisdiction',
                    title: 'Jurisdiction',
                    blocks: [
                        p('You agree that any disputes arising under these Terms shall be resolved in the state or federal courts located in San Francisco, California, USA.'),
                    ],
                }),
                section({
                    id: 'dispute-resolution',
                    title: 'Dispute Resolution',
                    blocks: [
                        p([
                            text('Before initiating any legal proceedings, you agree to attempt to resolve the dispute informally by contacting '),
                            link(supportMailto, supportEmail),
                            text('. If the dispute is not resolved within 30 days, either party may then proceed with formal legal remedies. Alternatively, both parties agree to consider mediation or arbitration as a means to resolve disputes before seeking judicial intervention.'),
                        ]),
                    ],
                }),
            ],
        }),
        section({
            id: 'changes-to-these-terms',
            title: '12. Changes to These Terms',
            blocks: [
                p('We reserve the right to modify these Terms at any time. If we make significant changes, we will notify you by email or by posting a notice on the Service. Your continued use of the Service after the changes become effective will signify your acceptance of the revised Terms & Conditions.'),
            ],
        }),
        section({
            id: 'entire-agreement',
            title: '13. Entire Agreement',
            blocks: [
                p('These Terms constitute the entire agreement between you and us regarding the use of the Service and supersede any prior agreements.'),
            ],
        }),
        section({
            id: 'severability-and-waiver',
            title: '14. Severability and Waiver',
            blocks: [
                p('If any provision of these Terms is deemed invalid or unenforceable, the remaining provisions will remain in full force and effect. Failure to enforce any right or provision does not constitute a waiver of such right or provision.'),
            ],
        }),
    ],
});
//# sourceMappingURL=terms.js.map